/**
 * Tenant Context Provider
 * Provides tenant configuration and state to EdgeMail Admin components
 */
import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { useTenantConfig, useUpdateTenantConfig } from '../hooks/useAdminDashboard';
import type { TenantConfig, EdgeMailTenant, TenantLimits, ComplianceSettings } from '../types/admin.types';

// Context types
interface TenantContextValue {
  // Tenant data
  tenant: EdgeMailTenant | null;
  limits: TenantLimits | null;
  features: Record<string, boolean>;
  complianceSettings: ComplianceSettings | null;

  // Loading states
  isLoading: boolean;
  isError: boolean;
  error: Error | null;

  // Actions
  updateConfig: (config: Record<string, unknown>) => Promise<void>;
  isUpdating: boolean;

  // Helpers
  hasFeature: (feature: keyof TenantLimits['features']) => boolean;
  getStorageLimitGB: () => number;
  getMailboxLimit: () => number;
  canAddMailbox: (currentCount: number) => boolean;
  canAddDomain: (currentCount: number) => boolean;
}

// Default context value
const defaultContextValue: TenantContextValue = {
  tenant: null,
  limits: null,
  features: {},
  complianceSettings: null,
  isLoading: true,
  isError: false,
  error: null,
  updateConfig: async () => {},
  isUpdating: false,
  hasFeature: () => false,
  getStorageLimitGB: () => 0,
  getMailboxLimit: () => 0,
  canAddMailbox: () => false,
  canAddDomain: () => false,
};

// Create context
const TenantContext = createContext<TenantContextValue>(defaultContextValue);

// Provider props
interface TenantProviderProps {
  children: React.ReactNode;
}

/**
 * Tenant Context Provider Component
 * Wraps EdgeMail Admin components to provide tenant configuration
 */
export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const { data: config, isLoading, isError, error } = useTenantConfig();
  const updateMutation = useUpdateTenantConfig();

  // Extract tenant data
  const tenant = config?.tenant ?? null;
  const limits = config?.limits ?? null;
  const features = config?.features ?? {};
  const complianceSettings = tenant?.compliance_settings ?? null;

  // Update config action
  const updateConfig = useCallback(
    async (newConfig: Record<string, unknown>) => {
      await updateMutation.mutateAsync(newConfig);
    },
    [updateMutation]
  );

  // Check if a feature is enabled
  const hasFeature = useCallback(
    (feature: keyof TenantLimits['features']): boolean => {
      if (!limits?.features) return false;
      return limits.features[feature] ?? false;
    },
    [limits]
  );

  // Get storage limit in GB
  const getStorageLimitGB = useCallback((): number => {
    return limits?.max_storage_gb ?? 0;
  }, [limits]);

  // Get mailbox limit
  const getMailboxLimit = useCallback((): number => {
    return limits?.max_mailboxes ?? 0;
  }, [limits]);

  // Check if can add more mailboxes
  const canAddMailbox = useCallback(
    (currentCount: number): boolean => {
      const limit = limits?.max_mailboxes ?? 0;
      return limit === 0 || currentCount < limit; // 0 means unlimited
    },
    [limits]
  );

  // Check if can add more domains
  const canAddDomain = useCallback(
    (currentCount: number): boolean => {
      const limit = limits?.max_domains ?? 0;
      return limit === 0 || currentCount < limit; // 0 means unlimited
    },
    [limits]
  );

  // Memoize context value
  const contextValue = useMemo<TenantContextValue>(
    () => ({
      tenant,
      limits,
      features,
      complianceSettings,
      isLoading,
      isError,
      error: error as Error | null,
      updateConfig,
      isUpdating: updateMutation.isPending,
      hasFeature,
      getStorageLimitGB,
      getMailboxLimit,
      canAddMailbox,
      canAddDomain,
    }),
    [
      tenant,
      limits,
      features,
      complianceSettings,
      isLoading,
      isError,
      error,
      updateConfig,
      updateMutation.isPending,
      hasFeature,
      getStorageLimitGB,
      getMailboxLimit,
      canAddMailbox,
      canAddDomain,
    ]
  );

  return <TenantContext.Provider value={contextValue}>{children}</TenantContext.Provider>;
};

/**
 * Hook to access tenant context
 * Must be used within a TenantProvider
 */
export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);

  if (context === defaultContextValue) {
    throw new Error('useTenant must be used within a TenantProvider');
  }

  return context;
}

/**
 * Hook to check if a specific feature is enabled
 * Convenience wrapper around useTenant().hasFeature
 */
export function useFeatureFlag(feature: keyof TenantLimits['features']): boolean {
  const { hasFeature, isLoading } = useTenant();

  // Return false while loading to prevent feature flash
  if (isLoading) return false;

  return hasFeature(feature);
}

/**
 * Hook to get tenant limits
 * Returns null while loading
 */
export function useTenantLimits(): TenantLimits | null {
  const { limits } = useTenant();
  return limits;
}

/**
 * Hook to get compliance settings
 * Returns null while loading
 */
export function useComplianceSettings(): ComplianceSettings | null {
  const { complianceSettings } = useTenant();
  return complianceSettings;
}

export default TenantContext;
